/**
 * Import-Validierungsframework
 * Prüft importierte Daten auf Fehler, Warnungen und gibt Korrekturvorschläge
 */

export type ValidationSeverity = 'error' | 'warning' | 'info';

export interface ValidationMessage {
  severity: ValidationSeverity;
  field: string;
  value: any;
  message: string;
  suggestion?: string;
  source?: string;
  row?: number;
  column?: string;
}

export interface ImportValidationResult {
  source: string;
  isValid: boolean;
  hasWarnings: boolean;
  messages: ValidationMessage[];
  data?: any;
}

export interface ImportValidationSummary {
  totalFiles: number;
  successCount: number;
  warningCount: number;
  errorCount: number;
  results: ImportValidationResult[];
}

export function formatValidationForClipboard(summary: ImportValidationSummary): string {
  const lines: string[] = [];

  lines.push('=== Import-Validierungsbericht ===');
  lines.push(`Datum: ${new Date().toLocaleString('de-DE')}`);
  lines.push('');
  lines.push(`Gesamt: ${summary.totalFiles} Dateien`);
  lines.push(`  ✓ Fehlerfrei: ${summary.successCount}`);
  lines.push(`  ⚠ Mit Warnungen: ${summary.warningCount}`);
  lines.push(`  ✗ Mit Fehlern: ${summary.errorCount}`);
  lines.push('');

  for (const result of summary.results) {
    if (result.messages.length === 0) continue;

    const icon = !result.isValid ? '✗' : result.hasWarnings ? '⚠' : '✓';
    lines.push(`${icon} ${result.source}`);

    for (const msg of result.messages) {
      const prefix = msg.severity === 'error' ? '  [FEHLER]' :
                     msg.severity === 'warning' ? '  [WARNUNG]' : '  [INFO]';
      const location = msg.row ? ` (Zeile ${msg.row}${msg.column ? ', Spalte ' + msg.column : ''})` : '';
      lines.push(`${prefix} ${msg.field}${location}: ${msg.message}`);
      if (msg.value !== undefined && msg.value !== '') {
        lines.push(`          Wert: "${msg.value}"`);
      }
      if (msg.suggestion) {
        lines.push(`          Vorschlag: ${msg.suggestion}`);
      }
    }
    lines.push('');
  }

  return lines.join('\n');
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Fehler beim Kopieren in Zwischenablage:', err);
    return false;
  }
}

export function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

export function findSimilar(value: string, validValues: string[], maxDistance = 2): string | null {
  if (!value) return null;

  const valueLower = value.toLowerCase();
  let bestMatch: string | null = null;
  let bestDistance = maxDistance + 1;

  for (const valid of validValues) {
    const distance = levenshteinDistance(valueLower, valid.toLowerCase());
    if (distance < bestDistance) {
      bestDistance = distance;
      bestMatch = valid;
    }
  }

  return bestDistance <= maxDistance ? bestMatch : null;
}
